/* global google */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useJsApiLoader, GoogleMap, MarkerClusterer, Marker, InfoWindow } from '@react-google-maps/api';
import { initialState } from '../../../../store/reducers';
import { googleMapsLoaderOptions } from '../../../../utils/googleMaps';

const mapContainerStyle = { height: '100%' };

const clustererOptions = {
    averageCenter: true,
    enableRetinaIcons: false,
    zoomOnClick: true,
    gridSize: 40,
    minimumClusterSize: 8,
    clusterClass: 'cluster',
};

class MapWithAMarkerClusterer extends Component {
    constructor(props) {
        super(props);

        this.state = {
            ready: false,
        };

        // The zoom is only set when the map is created (uncontrolled), passing it as a prop
        // would trigger zoom_changed -> getVisibleMarkers as soon as the map loads
        this.mapOptions = {
            zoom: props.map.zoom,
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
        };
    }

    componentDidUpdate(prevProps) {
        const { properties, map } = this.props;
        // Prevent the "ReferenceError: google is not defined" error and running fitBounds if map isn't loaded
        const hasLoaded = this.state.ready;
        // Prevent running fitBounds if content is being fetched
        const hasProperties =
            properties && prevProps.properties && properties.length > 0 && prevProps.properties.length > 0;
        // Check if the property list or visible markers have changed, this is also if search or filters have changed
        const currPropertiesSame = properties.every(
            property => prevProps.properties.findIndex(item => item._id === property._id) > -1
        );
        const prevPropertiesSame = prevProps.properties.every(
            property => properties.findIndex(item => item._id === property._id) > -1
        );
        const hasMarkersChanged = !currPropertiesSame || !prevPropertiesSame;
        // Check if map has been reset with no changes to visible markers (i.e. click on cluster then reset)
        const hasZoomChanged =
            map.center.lat === initialState.office.map.center.lat &&
            map.center.lng === initialState.office.map.center.lng &&
            map.zoom === initialState.office.map.zoom;

        if (hasLoaded) {
            if (hasMarkersChanged || (hasZoomChanged && hasProperties)) {
                this.fitBoundsToVisibleMarkers();
            }
        }
    }

    onMapLoad = map => {
        this.map = map;
        this.setState({ ready: true });
    };

    // zoom_changed fires before the zoom animation ends; updating the markers at that point makes
    // the clusterer group them with the previous zoom's projection, so wait for the next idle
    onZoomChanged = () => {
        this.hasPendingZoom = true;
    };

    onIdle = () => {
        if (this.hasPendingZoom) {
            this.hasPendingZoom = false;
            this.getVisibleMarkers();
        }
    };

    fitBoundsToVisibleMarkers = () => {
        const bounds = new google.maps.LatLngBounds();

        // Get new bounds for visible markers
        this.props.properties.forEach(property => {
            if (property.showMarker) {
                const position = { lat: property.geo[0], lng: property.geo[1] };

                bounds.extend(position);
            }
        });

        // Don't zoom in too far when only one marker
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            const extendPoint1 = new google.maps.LatLng(
                bounds.getNorthEast().lat() + 0.001,
                bounds.getNorthEast().lng() + 0.001
            );
            const extendPoint2 = new google.maps.LatLng(
                bounds.getNorthEast().lat() - 0.001,
                bounds.getNorthEast().lng() - 0.001
            );

            bounds.extend(extendPoint1);
            bounds.extend(extendPoint2);
        }

        // Update map bounds
        this.map.fitBounds(bounds, { left: 50, top: 50, right: 50, bottom: 50 });
    };

    getVisibleMarkers = () => {
        // Don't update markers if there are none or the map isn't ready
        if (!this.map || this.props.properties.length === 0) return;
        // Get the current map bounds
        const bounds = this.map.getBounds();

        if (!bounds) return;
        // Check if each property is in the map bounds
        const visibleProperties = this.props.properties.map(property => {
            const position = { lat: property.geo[0], lng: property.geo[1] };
            const showMarker = bounds.contains(position) === true;

            return {
                ...property,
                showMarker,
            };
        });
        // Get the current zoom and center
        const zoom = this.map.getZoom();
        const centerFromBounds = bounds.getCenter();
        const center = { lat: centerFromBounds.lat(), lng: centerFromBounds.lng() };

        // Update the visibleProperties, zoom and center in Office state
        this.props.searchMap(visibleProperties, zoom, center);
    };

    render() {
        const { properties, map, handleMapClick, handleMapOver } = this.props;

        return (
            <div style={mapContainerStyle} id="map-container-element">
                <GoogleMap
                    id="map"
                    mapContainerStyle={mapContainerStyle}
                    center={map.center}
                    options={this.mapOptions}
                    onLoad={this.onMapLoad}
                    onDragEnd={this.getVisibleMarkers}
                    onZoomChanged={this.onZoomChanged}
                    onIdle={this.onIdle}
                >
                    <MarkerClusterer options={clustererOptions}>
                        {clusterer => properties
                            .filter(property => property.showMarker)
                            .map(property => {
                                const isMarkerShown = map.propertyId === property._id;
                                const isInfoWindowShown = map.infoWindowId === property._id;
                                // Create marker icon using SVG Path notation for IE11
                                const markerIcon = {
                                    path: 'M-12,0a12,12 0 1,0 24,0a12,12 0 1,0 -24,0',
                                    fillColor: isMarkerShown ? '#26588d' : '#fdbe11',
                                    fillOpacity: 1,
                                    anchor: new google.maps.Point(0, 0),
                                    strokeWeight: 0,
                                    scale: 1,
                                };

                                return (
                                    <Marker
                                        key={property._id}
                                        clusterer={clusterer}
                                        position={{ lat: property.geo[0], lng: property.geo[1] }}
                                        icon={markerIcon}
                                        onMouseOver={() => handleMapOver(property._id, map.infoWindowId)}
                                        onClick={() => handleMapClick(property._id, true)}
                                        zIndex={isMarkerShown ? 2 : 1}
                                    >
                                        {isInfoWindowShown && (
                                            <InfoWindow onCloseClick={() => handleMapClick(null)}>
                                                <div className="info-window">
                                                    <h2 className="name">{property.name}</h2>
                                                    <p className="street-address">{property.street_address}</p>
                                                    <a href={`/office/${property.slug}`} className="view-property">
                                                        VIEW PROPERTY
                                                    </a>
                                                </div>
                                            </InfoWindow>
                                        )}
                                    </Marker>
                                );
                            })}
                    </MarkerClusterer>
                </GoogleMap>
            </div>
        );
    }
}

MapWithAMarkerClusterer.propTypes = {
    properties: PropTypes.array.isRequired,
    map: PropTypes.object.isRequired,
    searchMap: PropTypes.func.isRequired,
    handleMapClick: PropTypes.func.isRequired,
    handleMapOver: PropTypes.func.isRequired,
};

const PropertyMap = ({ properties, map, searchMap, handleMapClick, handleMapOver }) => {
    const { isLoaded } = useJsApiLoader(googleMapsLoaderOptions);

    if (!isLoaded) {
        return <div style={mapContainerStyle} id="map-loading-element" />;
    }

    return (
        <MapWithAMarkerClusterer
            properties={properties}
            map={map}
            searchMap={searchMap}
            handleMapClick={handleMapClick}
            handleMapOver={handleMapOver}
        />
    );
};

PropertyMap.propTypes = {
    properties: PropTypes.arrayOf(
        PropTypes.shape({
            _id: PropTypes.string.isRequired,
            geo: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
            name: PropTypes.string.isRequired,
            street_address: PropTypes.string.isRequired,
            slug: PropTypes.string.isRequired,
            showMarker: PropTypes.bool.isRequired,
        })
    ).isRequired,
    filters: PropTypes.object,
    map: PropTypes.shape({
        zoom: PropTypes.number.isRequired,
        center: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        }).isRequired,
        propertyId: PropTypes.string,
        infoWindowId: PropTypes.string,
    }).isRequired,
    searchMap: PropTypes.func.isRequired,
    handleMapClick: PropTypes.func.isRequired,
    handleMapOver: PropTypes.func.isRequired,
};

export default PropertyMap;
